// src/Dashboard.js
// Reads studentId from the URL (/dashboard/:studentId) instead of hardcoding it,
// then fetches both the student's name and their match results on mount.
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API_URL, authHeaders } from "./config";
import { useAuth } from './AuthContext';
import AppHeader from './AppHeader';

const PAGE_SIZE = 100;

const TRACKER_OPTIONS = [
  { value: "not_started", label: "Not Started", color: "#E67E22" },
  { value: "in_progress", label: "In Progress", color: "#2E75B6" },
  { value: "submitted", label: "Submitted", color: "#8E44AD" },
  { value: "awarded", label: "Awarded 🏆", color: "#1D9E75" },
];

function ScoreBadge({ score }) {
  const color = score >= 90 ? "#1D9E75" : score >= 75 ? "#2E75B6" : score >= 60 ? "#E67E22" : "#8A95A3";
  const bg = score >= 90 ? "#D5F5E3" : score >= 75 ? "#D6E4F0" : score >= 60 ? "#FEF5E7" : "#F4F6F8";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <div style={{ width: 44, height: 44, borderRadius: "50%", background: bg, border: `2px solid ${color}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color }}>{score}</span>
      </div>
    </div>
  );
}

function CriteriaGrid({ criteria }) {
  const labels = { geographic: "Geographic", gpa: "GPA", major: "Major", financial_need: "Financial", demographics: "Eligibility", test_scores: "Test Scores" };
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
      {Object.entries(criteria).map(([key, passed]) => {
        if (!labels[key]) return null;
        return (
          <span key={key} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, background: passed ? "#D5F5E3" : "#FADBD8", color: passed ? "#1A6B3A" : "#922B21", fontWeight: 500 }}>
            {passed ? "✓" : "✗"} {labels[key]}
          </span>
        );
      })}
    </div>
  );
}

function formatAmount(min, max) {
  const numMin = parseFloat(min) || 0;
  const numMax = parseFloat(max) || 0;
  if (!numMin && !numMax) return "Varies";
  if (numMin && !numMax) return `$${numMin.toLocaleString()}`;
  if (!numMin && numMax) return `Up to $${numMax.toLocaleString()}`;
  if (numMin === numMax) return `$${numMin.toLocaleString()}`;
  return `$${numMin.toLocaleString()} – $${numMax.toLocaleString()}`;
}
const getComplexityInfo = (match) => {
  const essay = match.essay_required;
  const letters = match.letters_required;
  const financial = match.financial_need_based;

  if (essay && letters && financial) {
    return { emoji: '📑', label: 'Extended', color: '#dc2626', bg: '#fef2f2' };
  }
  if (essay && letters) {
    return { emoji: '📋', label: 'Involved', color: '#ea580c', bg: '#fff7ed' };
  }
  if (essay || letters) {
    return { emoji: '📝', label: 'Standard', color: '#2563eb', bg: '#eff6ff' };
  }
  return { emoji: '⚡', label: 'Quick Apply', color: '#16a34a', bg: '#f0fdf4' };
};

function getApplySteps(match) {
  const steps = [];
  steps.push({ n: 1, text: "Visit the scholarship website to begin your application" });
  if (match.financial_need_based) steps.push({ n: steps.length + 1, text: "Gather financial documents (tax returns, FAFSA info)" });
  if (match.essay_required) steps.push({ n: steps.length + 1, text: "Write and proofread your application essay" });
  if (match.letters_required) steps.push({ n: steps.length + 1, text: "Request letters of recommendation" });
  steps.push({ n: steps.length + 1, text: "Complete and submit your application before the deadline" });
  if (match.application_url) steps.push({ n: steps.length + 1, text: "Track your application status on the provider's website" });
  return steps;
}

function ScholarshipCard({ match, onTrack, studentId }) {
  const [expanded, setExpanded] = useState(false);
  const [trackerVal, setTrackerVal] = useState(match.tracker_status || "");
  const isExcluded = match.status === "excluded";
  const amount = formatAmount(match.amount_min, match.amount_max);
  const deadlineDate = match.deadline
    ? new Date(match.deadline.slice(0, 10).replace(/-/g, "/"))
    : null;
  const daysUntil = deadlineDate ? Math.ceil((deadlineDate - new Date()) / (1000 * 60 * 60 * 24)) : null;
  const deadline = deadlineDate ? deadlineDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "Rolling";
  const isUrgent = daysUntil !== null && daysUntil <= 30;
  const isSoon = daysUntil !== null && daysUntil <= 60 && daysUntil > 30;

  const openDate = match.application_open_date
    ? new Date(match.application_open_date.slice(0, 10).replace(/-/g, "/"))
    : null;
  const isOpeningSoon = openDate && openDate > new Date();
  const openDateFormatted = openDate ? openDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : null;

  // Sync local tracker value when parent state changes (e.g. after optimistic update)
  useEffect(() => {
    setTrackerVal(match.tracker_status || "");
  }, [match.tracker_status]);

  return (
    <div style={{ background: "#fff", border: `1px solid ${isExcluded ? "#FADBD8" : "#E2E8F0"}`, borderLeft: `4px solid ${isExcluded ? "#E74C3C" : match.relevance_score >= 90 ? "#1D9E75" : match.relevance_score >= 75 ? "#2E75B6" : "#E67E22"}`, borderRadius: 10, padding: "16px 18px", marginBottom: 12, opacity: isExcluded ? 0.75 : 1, transition: "box-shadow .2s" }}>
      <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
        {!isExcluded && <ScoreBadge score={match.relevance_score} />}
        {isExcluded && (
          <div style={{ width: 44, height: 44, borderRadius: "50%", background: "#FADBD8", border: "2px solid #E74C3C", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 18 }}>✗</div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, color: "#1C2833", marginBottom: 2 }}>{match.name}</div>
              <div style={{ fontSize: 12, color: "#8A95A3", marginBottom: 6 }}>{match.provider_name}</div>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
          
            </div>
          </div>

          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#1D9E75" }}>{amount}</span>
            {isOpeningSoon ? (
              <span style={{ fontSize: 13, fontWeight: 600, color: "#2E75B6", background: "#EBF5FB", padding: "2px 8px", borderRadius: 4 }}>
                📅 Opens {openDateFormatted}{deadlineDate ? ` · Due ${deadline}` : ""}
              </span>
            ) : (
              <span style={{ fontSize: 13, fontWeight: isUrgent || isSoon ? 600 : 400, color: isUrgent ? "#C0392B" : isSoon ? "#E67E22" : "#8A95A3", background: isUrgent ? "#FADBD8" : isSoon ? "#FEF5E7" : "transparent", padding: isUrgent || isSoon ? "2px 8px" : "0", borderRadius: 4 }}>
                {isUrgent ? "⚠️ " : isSoon ? "⏰ " : ""}Due {deadline}
              </span>
            )}
            {!isExcluded && (() => {
              const c = getComplexityInfo(match);
              return (
                <>
                 <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, background: c.bg, color: c.color, fontWeight: 600 }}>
                  {c.emoji} {c.label}
                 </span>
                 {match.essay_required && (
                   <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: '#f1f5f9', color: '#475569' }}>Essay</span>
                  )}
                 {match.letters_required && (
                   <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: '#f1f5f9', color: '#475569' }}>Letters of Rec</span>
                  )}
                 {match.financial_need_based && (
                   <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: '#f1f5f9', color: '#475569' }}>Financial Docs</span>
                  )}
                </>
              );
            })()}
          </div>
          {match.match_summary && (
            <div style={{ fontSize: 13, color: "#2E75B6", background: "#EBF5FB", padding: "8px 12px", borderRadius: 6, marginBottom: 8, lineHeight: 1.5 }}>
              💡 {match.match_summary}
            </div>
          )}
          {match.exclusion_reason && (
            <div style={{ fontSize: 13, color: "#922B21", background: "#FADBD8", padding: "8px 12px", borderRadius: 6, marginBottom: 8, lineHeight: 1.5 }}>
              ✗ {match.exclusion_reason}
            </div>
          )}

          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            {!isExcluded && (
                            <select
                value={trackerVal}
                onChange={e => {
                  const val = e.target.value;
                  if (val === "") return;
                  if (val === "untrack") {
                    setTrackerVal("");
                    onTrack(match.scholarship_id, "untrack");
                  } else {
                    setTrackerVal(val);
                    onTrack(match.scholarship_id, val);
                  }
                }}
                style={{ fontSize: 12, padding: "4px 10px", borderRadius: 6, border: "1px solid #D8DEE4", background: "#F4F6F8", color: "#4A5568", cursor: "pointer" }}
              >
                <option value="">Track this ▾</option>
                {TRACKER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                {trackerVal && <option value="untrack">── Untrack ──</option>}
              </select>
            )}
            <button
              onClick={() => setExpanded(!expanded)}
              style={{ fontSize: 12, color: "#2E75B6", background: "none", border: "none", cursor: "pointer", padding: 0 }}
            >
              {expanded ? "Hide criteria ↑" : "Show criteria breakdown ↓"}
            </button>
            {!isExcluded && match.application_url && (
            <a href={match.application_url} target="_blank" rel="noreferrer" onClick={() => { fetch(`${API_URL}/api/application-tracker/auto-advance`, { method: 'POST', keepalive: true, headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ studentId, scholarshipId: match.scholarship_id }) }).catch(() => {}); if (!trackerVal || trackerVal === 'saved' || trackerVal === 'not_started') { setTrackerVal('in_progress'); onTrack(match.scholarship_id, 'in_progress'); } }} style={{ fontSize: 13, color: "white", fontWeight: 600, background: "#1D9E75", padding: "6px 14px", borderRadius: 6, textDecoration: "none" }}>Apply →</a>
            )}
          </div>

          {expanded && match.criteria_results && (
            <CriteriaGrid criteria={match.criteria_results} />
          )}
          {expanded && (
            <div style={{ marginTop: 12, padding: "12px 14px", background: "#f8faff", borderRadius: 6, border: "1px solid #dce8f5" }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: "#1F4E79", textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 10px", fontFamily: "sans-serif" }}>How to Apply</p>
              <ol style={{ margin: 0, padding: "0 0 0 18px" }}>
                {getApplySteps(match).map(s => (
                  <li key={s.n} style={{ fontSize: 12, color: "#2c3e50", marginBottom: 6, fontFamily: "sans-serif", lineHeight: 1.5 }}>{s.text}</li>
                ))}
              </ol>
              {match.application_url && (
                <a href={match.application_url} target="_blank" rel="noreferrer" onClick={() => { fetch(`${API_URL}/api/application-tracker/auto-advance`, { method: 'POST', keepalive: true, headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ studentId, scholarshipId: match.scholarship_id }) }).catch(() => {}); if (!trackerVal || trackerVal === 'saved' || trackerVal === 'not_started') setTrackerVal('in_progress'); }} style={{ display: "inline-block", marginTop: 10, fontSize: 12, color: "#2E75B6", fontWeight: 600, textDecoration: "none" }}>
                  Go to application →
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
      const { studentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [matches, setMatches] = useState([]);
  const [counts, setCounts] = useState({});
  const [trackerCounts, setTrackerCounts] = useState({});
  const [totalActive, setTotalActive] = useState(0);
  const [evaluated, setEvaluated] = useState(0);
  const [matchingComplete, setMatchingComplete] = useState(true);
  const [studentName, setStudentName] = useState("");
  const [filter, setFilter] = useState("included");
  const [platformFilter] = useState("all");
  const [minScore, setMinScore] = useState(0);
  const [deadlineFilter, setDeadlineFilter] = useState(0);
  const [openWithinFilter, setOpenWithinFilter] = useState(0);
  const [sortBy, setSortBy] = useState("score");
  const [trackerFilter, setTrackerFilter] = useState("");
  // Category filters (#32)
  const [minAmount, setMinAmount] = useState("");
  const [noEssay, setNoEssay] = useState(false);
  const [meritFilter, setMeritFilter] = useState(false);
  const [needFilter, setNeedFilter] = useState(false);
  const [majorFilter, setMajorFilter] = useState("");
  const [athleticFilter, setAthleticFilter] = useState(false);
  const [minoritiesFilter, setMinoritiesFilter] = useState(false);
  const [firstGenFilter, setFirstGenFilter] = useState(false);
  const [militaryFilter, setMilitaryFilter] = useState(false);
  const [religiousFilter, setReligiousFilter] = useState(false);
  const [disabilityFilter, setDisabilityFilter] = useState(false);
  const [lgbtqFilter, setLgbtqFilter] = useState(false);
  const [rotcFilter, setRotcFilter] = useState(false);
  const [singleParentFilter, setSingleParentFilter] = useState(false);
  const [noGpaFilter, setNoGpaFilter] = useState(false);
  const [residencyFilter, setResidencyFilter] = useState(false);
  const [yearLevelFilter, setYearLevelFilter] = useState("");
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [institutionFilter, setInstitutionFilter] = useState("");
  const [institutionSearch, setInstitutionSearch] = useState("");
  const [institutionOptions, setInstitutionOptions] = useState([]);
  const [showInstitutionDropdown, setShowInstitutionDropdown] = useState(false);
  const TOP_MAJORS = ["Engineering/Technology","Education","Business/Consumer Services","Nursing","Health and Medical Sciences","Science, Technology, and Society","Mathematics","Accounting","Arts","Music","Agriculture","Aviation/Aerospace","Computer Science/Data Processing","Law/Legal Studies","Biology","Civil Engineering","Journalism","Performing Arts","Environmental Science","Electrical Engineering/Electronics"];
  // Institution search — debounced API lookup
  useEffect(() => {
    if (institutionSearch.length < 2) { setInstitutionOptions([]); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${API_URL}/api/matches/institutions?q=${encodeURIComponent(institutionSearch)}`, { headers: authHeaders() });
        if (res.ok) { const data = await res.json(); setInstitutionOptions(data); setShowInstitutionDropdown(true); }
      } catch (e) { console.error('Institution search error:', e); }
    }, 300);
    return () => clearTimeout(timer);
  }, [institutionSearch]);
  const hasActiveFilters = trackerFilter || minAmount || noEssay || meritFilter || needFilter || majorFilter || institutionFilter || athleticFilter || minoritiesFilter || firstGenFilter || militaryFilter || religiousFilter || disabilityFilter || lgbtqFilter || rotcFilter || singleParentFilter || noGpaFilter || residencyFilter || yearLevelFilter || deadlineFilter > 0;
  const clearCategoryFilters = () => {
    setMinAmount(""); setNoEssay(false); setMeritFilter(false); setNeedFilter(false);
    setMajorFilter(""); setInstitutionFilter(""); setInstitutionSearch(""); setAthleticFilter(false); setMinoritiesFilter(false);
    setFirstGenFilter(false); setMilitaryFilter(false); setReligiousFilter(false);
    setDisabilityFilter(false); setLgbtqFilter(false); setRotcFilter(false);
    setSingleParentFilter(false); setNoGpaFilter(false); setResidencyFilter(false);
    setYearLevelFilter(""); setTrackerFilter(""); setDeadlineFilter(0); setShowMoreFilters(false);
  };
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [needsRefresh, setNeedsRefresh] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);
  const [statusMsgIndex, setStatusMsgIndex] = useState(0);

  // Derive display counts from API counts (accurate totals) 
  const includedCount = counts.included || 0;
  const excludedCount = counts.excluded || 0;

  const buildFetchUrl = useCallback((status, pageOffset) => {
    let url = `${API_URL}/api/matches/${studentId}?status=${status}&limit=${PAGE_SIZE}&offset=${pageOffset}`;
    if (minScore > 0) url += `&min_score=${minScore}`;
    if (platformFilter !== "all") url += `&platform=${platformFilter}`;
    url += `&open_within=${openWithinFilter}`;
    if (minAmount) url += `&min_amount=${minAmount}`;
    if (noEssay) url += `&no_essay=true`;
    if (meritFilter) url += `&merit=true`;
    if (needFilter) url += `&need=true`;
    if (majorFilter) url += `&major=${encodeURIComponent(majorFilter)}`;
    if (institutionFilter) url += `&institution=${encodeURIComponent(institutionFilter)}`;
    if (athleticFilter) url += `&athletic=true`;
    if (minoritiesFilter) url += `&minorities=true`;
    if (firstGenFilter) url += `&first_gen=true`;
    if (militaryFilter) url += `&military=true`;
    if (religiousFilter) url += `&religious=true`;
    if (disabilityFilter) url += `&disability=true`;
    if (lgbtqFilter) url += `&lgbtq=true`;
    if (rotcFilter) url += `&rotc=true`;
    if (singleParentFilter) url += `&single_parent=true`;
    if (noGpaFilter) url += `&no_gpa=true`;
    if (residencyFilter) url += `&residency=true`;
    if (yearLevelFilter) url += `&year_level=${yearLevelFilter}`;
    if (trackerFilter) url += `&tracker_status=${trackerFilter}`;
    if (deadlineFilter > 0) url += `&deadline_within=${deadlineFilter}`;
    return url;
  }, [studentId, minScore, platformFilter, openWithinFilter, minAmount, noEssay, meritFilter, needFilter, majorFilter, institutionFilter, athleticFilter, minoritiesFilter, firstGenFilter, militaryFilter, religiousFilter, disabilityFilter, lgbtqFilter, rotcFilter, singleParentFilter, noGpaFilter, residencyFilter, yearLevelFilter, trackerFilter, deadlineFilter]);

  // Load student name + first page of matches on mount
  useEffect(() => {
    if (!studentId) return;
    let cancelled = false;

    async function loadAll() {
      setLoading(true);
      setLoadError(null);
      setMatches([]);
      setOffset(0);
      setHasMore(true);
      try {
        const [studentRes, matchRes] = await Promise.all([
          fetch(`${API_URL}/api/students/${studentId}`, { headers: authHeaders() }),
          fetch(buildFetchUrl(filter, 0), { headers: authHeaders() }),
        ]);

        if (!studentRes.ok) throw new Error(`Student lookup failed (${studentRes.status})`);
        if (!matchRes.ok) throw new Error(`Match lookup failed (${matchRes.status})`);

        const studentData = await studentRes.json();
        const matchData = await matchRes.json();

        if (cancelled) return;
        setStudentName(studentData.full_name || "");
        setMatches(matchData.results || []);
        setCounts(matchData.counts || {});
        setTrackerCounts(matchData.tracker_counts || {});
        setTotalActive(matchData.total_active || 0);
        setEvaluated(matchData.evaluated || 0);
        setMatchingComplete(matchData.matching_complete !== false);
        setOffset(PAGE_SIZE);
        setHasMore((matchData.results || []).length === PAGE_SIZE);
      } catch (err) {
        if (!cancelled) setLoadError(err.message || "Failed to load dashboard data.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

        loadAll();
    return () => { cancelled = true; };
  }, [studentId, filter, minScore, platformFilter, buildFetchUrl, reloadKey]);

  // Poll for progress while matching is running
  // IMPORTANT: Only update progress counters during polling — do NOT replace
  // the matches array. This keeps the student's current view stable while
  // they browse. A full reload happens once matching completes.
  useEffect(() => {
    if (matchingComplete || !studentId) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(buildFetchUrl(filter, 0), { headers: authHeaders() });
        const data = await res.json();

        // Always update progress counters
        setCounts(data.counts || {});
        setTrackerCounts(data.tracker_counts || {});
        setTotalActive(data.total_active || 0);
        setEvaluated(data.evaluated || 0);

       if (data.matching_complete) {
          setMatchingComplete(true);
          setNeedsRefresh(true);
          clearInterval(interval);
        }
        // While still matching: do NOT call setMatches — leave the
        // student's current view untouched
      } catch (err) { /* silent retry */ }
        }, 5000);
    return () => clearInterval(interval);
  }, [matchingComplete, studentId, filter, buildFetchUrl]);

    // Rotate status messages during matching
  const STATUS_MESSAGES = [
    "🔄 Matching in progress...",
    "📖 Reading scholarship descriptions...",
    "🎯 Checking eligibility criteria...",
    "🔍 Analyzing profile match...",
    "📊 Scoring and ranking results...",
    "🏫 Checking institution requirements...",
    "📋 Evaluating application requirements...",
  ];
  useEffect(() => {
    if (matchingComplete) return;
    const timer = setInterval(() => {
      setStatusMsgIndex(i => (i + 1) % STATUS_MESSAGES.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [matchingComplete, STATUS_MESSAGES.length]);
  async function loadMore() {
    if (!studentId || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await fetch(buildFetchUrl(filter, offset), { headers: authHeaders() });
      const data = await res.json();
      const newResults = data.results || [];
      setMatches(prev => [...prev, ...newResults]);
      setOffset(prev => prev + PAGE_SIZE);
      setHasMore(newResults.length === PAGE_SIZE);
      if (data.counts) setCounts(data.counts);
      if (data.tracker_counts) setTrackerCounts(data.tracker_counts);
      if (data.total_active) setTotalActive(data.total_active);
      if (data.evaluated !== undefined) setEvaluated(data.evaluated);
      if (data.matching_complete !== undefined) setMatchingComplete(data.matching_complete);
    } catch (err) {
      setLoadError("Could not load more results.");
    } finally {
      setLoadingMore(false);
    }
  }

    async function onTrack(scholarshipId, newStatus) {
    if (!studentId) return;

    // Find the old status for optimistic count update
    const match = matches.find(m => m.scholarship_id === scholarshipId);
    const oldStatus = match?.tracker_status || null;

    if (newStatus === "untrack") {
      // Optimistically remove from counts and clear local status
      setTrackerCounts(prev => {
        const updated = { ...prev };
        if (oldStatus && updated[oldStatus] > 0) updated[oldStatus]--;
        return updated;
      });
      setMatches(prev => prev.map(m =>
        m.scholarship_id === scholarshipId ? { ...m, tracker_status: null } : m
      ));
      await fetch(`${API_URL}/api/matches/${scholarshipId}/track`, {
        method: "DELETE",
        keepalive: true,
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ student_id: studentId })
      }).catch(err => console.warn("Tracker untrack request failed", err));
      return;
    }

    // Optimistically update local tracker counts
    setTrackerCounts(prev => {
      const updated = { ...prev };
      if (oldStatus && updated[oldStatus] > 0) updated[oldStatus]--;
      updated[newStatus] = (updated[newStatus] || 0) + 1;
      return updated;
    });

    // Update the match's tracker_status in local state
    setMatches(prev => prev.map(m =>
      m.scholarship_id === scholarshipId ? { ...m, tracker_status: newStatus } : m
    ));

    // keepalive: the Apply link opens the scholarship site in the same tap; in-app
    // browsers (LinkedIn/FB/IG) cancel in-flight requests on navigation (FRONTEND-B).
    await fetch(`${API_URL}/api/matches/${scholarshipId}/track`, {
      method: "PATCH",
      keepalive: true,
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ student_id: studentId, status: newStatus })
    }).catch(err => console.warn("Tracker update request failed", err));
  }

  // Client-side safety net — deadline window filtering is now server-side (counts stay accurate)
  const filtered = matches.filter(m => {
    // Only apply past-deadline filtering for included matches, not excluded
    if (m.status !== "excluded" && m.deadline) {
      const days = Math.ceil((new Date(m.deadline.slice(0, 10).replace(/-/g, "/")) - new Date()) / (1000 * 60 * 60 * 24));
      if (days < 0) return false;
    }
    return true;
  });

  const getComplexityOrder = (m) => {
  const e = m.essay_required, l = m.letters_required, f = m.financial_need_based;
  if (e && l && f) return 3;
  if (e && l) return 2;
  if (e || l) return 1;
  return 0;
};

const sorted = [...filtered].sort((a, b) => {
  switch (sortBy) {
    case "amount":
      return (b.amount_max || 0) - (a.amount_max || 0);
    case "quick":
      return getComplexityOrder(a) - getComplexityOrder(b);
    case "deadline":
      if (!a.deadline && !b.deadline) return 0;
      if (!a.deadline) return 1;
      if (!b.deadline) return -1;
      return new Date(a.deadline) - new Date(b.deadline);
    default:
      return 0;
  }
});

    // Format dollar values — abbreviated ($3.9M / $45.2K / $500)
  const formatValue = (val) => {
    if (!val || val === 0) return null;
    if (val >= 1000000) return `~$${(val / 1000000).toFixed(1)}M`;
    if (val >= 1000) return `~$${(val / 1000).toFixed(1)}K`;
    return `~$${val.toLocaleString()}`;
  };

  // Summary cards — clickable, server-side counts (#64, #7 dollar values)
  const summaryCards = [
        {
      label: "Matched", value: includedCount, color: "#1D9E75", bg: "#D5F5E3",
      subtitle: formatValue(trackerCounts.value_included),
      isActive: filter === "included" && !trackerFilter,
      onClick: () => { setFilter("included"); setTrackerFilter(""); }
    },
    {
      label: "Filtered Out", value: excludedCount, color: "#E74C3C", bg: "#FADBD8",
      subtitle: null,
      isActive: filter === "excluded",
      onClick: () => { setFilter("excluded"); setTrackerFilter(""); }
    },
    {
      label: "In Progress", value: trackerCounts.in_progress || 0, color: "#2E75B6", bg: "#D6E4F0",
      subtitle: formatValue(trackerCounts.value_in_progress),
      isActive: trackerFilter === "in_progress",
      onClick: () => { if (trackerFilter === "in_progress") { setTrackerFilter(""); } else { setFilter("included"); setTrackerFilter("in_progress"); } }
    },
    {
      label: "Submitted", value: trackerCounts.submitted || 0, color: "#8E44AD", bg: "#F5EEF8",
      subtitle: formatValue(trackerCounts.value_submitted),
      isActive: trackerFilter === "submitted",
      onClick: () => { if (trackerFilter === "submitted") { setTrackerFilter(""); } else { setFilter("included"); setTrackerFilter("submitted"); } }
    },
    {
      label: "Awarded", value: trackerCounts.awarded || 0, color: "#D4AC0D", bg: "#FDF8E1",
      subtitle: formatValue(trackerCounts.value_awarded),
      isActive: trackerFilter === "awarded",
      onClick: () => { if (trackerFilter === "awarded") { setTrackerFilter(""); } else { setFilter("included"); setTrackerFilter("awarded"); } }
    },
  ];

  return (
        <>
      <AppHeader subtitle={studentName ? `Scholarship Dashboard — ${studentName}` : 'Scholarship Dashboard'} />
      {!user && (
        <div style={{ background: "#1F4E79", padding: "14px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 32, height: 32, background: "#2E75B6", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 14 }}>S</div>
            <div style={{ textAlign: "left" }}>
              <div style={{ color: "white", fontWeight: 600, fontSize: 16 }}>Your Scholarship Finder</div>
              <div style={{ color: "#AED6F1", fontSize: 11 }}>Scholarship Dashboard{studentName ? ` — ${studentName}` : ""}</div>
            </div>
          </div>
        </div>
      )}
    <div style={{ minHeight: "100vh", background: "#F0F4F8", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
      {/* Edit Profile bar */}
        <div style={{ maxWidth: 820, margin: "0 auto", padding: "12px 16px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {user?.role === 'parent' && (
          <button onClick={() => navigate('/parent/dashboard')} style={{ fontSize: 12, padding: "7px 14px", borderRadius: 6, background: "transparent", color: "#1F4E79", border: "1px solid #1F4E79", cursor: "pointer", fontWeight: 600 }}>
            ← Parent Dashboard
          </button>
        )}
        <button onClick={() => navigate(`/profile/edit/${studentId}`)} style={{ fontSize: 12, padding: "7px 14px", borderRadius: 6, background: "#1F4E79", color: "white", border: "none", cursor: "pointer", fontWeight: 600 }}>
          ✏️ Edit Profile
        </button>
      </div>

      <div style={{ maxWidth: 820, margin: "0 auto", padding: "24px 16px" }}>

        {/* Error banner */}
        {loadError && (
          <div style={{ background: "#FADBD8", border: "1px solid #E74C3C", borderRadius: 10, padding: "12px 16px", marginBottom: 16, fontSize: 13, color: "#922B21" }}>
            ⚠️ {loadError}
          </div>
        )}

        {/* Matching progress bar — v2: amber outline, sweeping animation, larger (Annette feedback S76) */}
        {!matchingComplete && totalActive > 0 && (
          <>
          <style>{`
            @keyframes ysfSweep { 0% { transform: translateX(-100%); } 100% { transform: translateX(250%); } }
            @keyframes ysfSpin { to { transform: rotate(360deg); } }
            @keyframes ysfBorder { 0%, 100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.45); } 50% { box-shadow: 0 0 0 6px rgba(245, 158, 11, 0); } }
            @media (prefers-reduced-motion: reduce) { .ysf-sweep, .ysf-spin, .ysf-banner { animation: none !important; } }
          `}</style>
          <div className="ysf-banner" role="status" aria-live="polite" style={{
            background: "#FFF7E6", borderRadius: 12, padding: "22px 24px", marginBottom: 20,
            border: "3px solid #F59E0B", animation: "ysfBorder 2s ease-in-out infinite"
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
              <span style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 19, fontWeight: 700, color: "#7C2D12" }}>
                <span className="ysf-spin" aria-hidden="true" style={{
                  width: 18, height: 18, borderRadius: "50%", border: "3px solid #FCD34D",
                  borderTopColor: "#D97706", display: "inline-block", animation: "ysfSpin 0.9s linear infinite"
                }} />
                Your scholarship list is still being compiled
              </span>
              <span style={{ fontSize: 18, fontWeight: 700, color: "#B45309", background: "#FDE68A", borderRadius: 999, padding: "4px 12px" }}>
                {Math.min(100, Math.round((evaluated / totalActive) * 100))}%
              </span>
            </div>
            <div style={{ position: "relative", width: "100%", height: 16, background: "#FDE7C2", borderRadius: 8, overflow: "hidden" }}>
              <div style={{
                width: `${Math.min(100, Math.round((evaluated / totalActive) * 100))}%`,
                height: "100%",
                background: "linear-gradient(90deg, #F59E0B, #EA580C)",
                borderRadius: 8,
                transition: "width 0.5s ease"
              }} />
              <div className="ysf-sweep" aria-hidden="true" style={{
                position: "absolute", top: 0, left: 0, height: "100%", width: "40%",
                background: "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.75) 50%, rgba(255,255,255,0) 100%)",
                animation: "ysfSweep 1.6s ease-in-out infinite"
              }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginTop: 12, flexWrap: "wrap" }}>
              <span style={{ fontSize: 15, fontWeight: 600, color: "#7C2D12" }}>
                Still working: {evaluated.toLocaleString()} of {totalActive.toLocaleString()} scholarships checked. Browse your results while we finish.
              </span>
              <span style={{ fontSize: 13, color: "#B45309", transition: "opacity 0.3s" }}>
                {STATUS_MESSAGES[statusMsgIndex]}
              </span>
            </div>
          </div>
          </>
        )}
        {/* Matching complete — prompt to refresh */}
        {needsRefresh && (
          <div style={{ background: "#D5F5E3", borderRadius: 10, padding: "14px 18px", marginBottom: 16, border: "1px solid #1D9E75", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#1A6B3A" }}>
              ✅ Matching complete! Your final results are ready.
            </span>
                        <button
              onClick={() => { setNeedsRefresh(false); setReloadKey(k => k + 1); }}
              style={{ fontSize: 13, fontWeight: 600, padding: "7px 16px", borderRadius: 6, background: "#1D9E75", color: "white", border: "none", cursor: "pointer" }}
            >
              Refresh Results
            </button>
          </div>
        )}
        {/* Summary cards — clickable, server-side counts (#64) */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12, marginBottom: 24 }}>
          {summaryCards.map(card => (
            <div
              key={card.label}
              onClick={card.onClick}
              style={{
                background: "white", borderRadius: 10, padding: "14px 16px",
                border: `1px solid ${card.bg}`, borderTop: `3px solid ${card.color}`,
                cursor: "pointer",
                outline: card.isActive ? `2px solid ${card.color}` : "none",
                outlineOffset: -1,
                transition: "outline 0.15s ease",
              }}
            >
                            <div style={{ fontSize: 26, fontWeight: 700, color: card.color }}>{card.value.toLocaleString()}</div>
              <div style={{ fontSize: 12, color: "#8A95A3", fontWeight: 500 }}>{card.label}</div>
              {card.subtitle && <div style={{ fontSize: 11, color: card.color, fontWeight: 600, marginTop: 2, opacity: 0.8 }}>{card.subtitle}</div>}
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{ background: "white", borderRadius: 10, padding: "14px 16px", marginBottom: 16, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", border: "1px solid #E2E8F0" }}>
          <div style={{ display: "flex", gap: 6 }}>
            {[["included", "✓ Matches"], ["excluded", "✗ Filtered Out"], ["all", "All"]].map(([val, label]) => (
              <button key={val} onClick={() => { setFilter(val); if (val !== "included") setTrackerFilter(""); }} style={{ fontSize: 12, padding: "6px 12px", borderRadius: 6, border: "1px solid", borderColor: filter === val ? "#1F4E79" : "#D8DEE4", background: filter === val ? "#1F4E79" : "white", color: filter === val ? "white" : "#4A5568", cursor: "pointer", fontWeight: filter === val ? 600 : 400 }}>
                {label}
              </button>
            ))}
          </div>  
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ fontSize: 12, padding: "6px 10px", borderRadius: 6, border: "1px solid #D8DEE4", background: "white", color: "#4A5568" }}>
            <option value="score">Sort: Best Match</option>
            <option value="amount">Sort: Highest $$</option>
            <option value="quick">Sort: Quickest Apply</option>
            <option value="deadline">Sort: Deadline Soonest</option>
          </select>
          <select value={minScore} onChange={e => setMinScore(Number(e.target.value))} style={{ fontSize: 12, padding: "6px 10px", borderRadius: 6, border: "1px solid #D8DEE4", background: "white", color: "#4A5568" }}>
            <option value={0}>All scores ({includedCount.toLocaleString()})</option>
            <option value={90}>90+ Excellent</option>
            <option value={75}>75+ Good</option>
            <option value={60}>60+ Decent</option>
          </select>
          {/* Tracker status filter (#64) */}
          {filter === "included" && (
            <select
              value={trackerFilter}
              onChange={e => setTrackerFilter(e.target.value)}
              style={{ fontSize: 12, padding: "6px 10px", borderRadius: 6, border: "1px solid", borderColor: trackerFilter ? "#2B7A4B" : "#D8DEE4", background: trackerFilter ? "#EBF5EE" : "white", color: trackerFilter ? "#2B7A4B" : "#4A5568" }}
            >
              <option value="">Application Status</option>
              <option value="saved">Saved ({trackerCounts.saved || 0})</option>
              <option value="not_started">Not Started ({trackerCounts.not_started || 0})</option>
              <option value="in_progress">In Progress ({trackerCounts.in_progress || 0})</option>
              <option value="submitted">Submitted ({trackerCounts.submitted || 0})</option>
              <option value="awarded">Awarded ({trackerCounts.awarded || 0})</option>
              <option value="untracked">Not Tracked</option>
            </select>
          )}
          <div style={{ display: "flex", gap: 6 }}>
            {[[0, "Any deadline"], [30, "Due in 30d"], [60, "Due in 60d"], [90, "Due in 90d"]].map(([val, label]) => (
              <button key={val} onClick={() => setDeadlineFilter(val)} style={{ fontSize: 12, padding: "6px 12px", borderRadius: 6, border: "1px solid", borderColor: deadlineFilter === val ? "#1F4E79" : "#D8DEE4", background: deadlineFilter === val ? "#1F4E79" : "white", color: deadlineFilter === val ? "white" : "#4A5568", cursor: "pointer", fontWeight: deadlineFilter === val ? 600 : 400 }}>
                {label}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {[[0, "Open now"], [30, "+30d"], [60, "+60d"], [90, "+90d"]].map(([val, label]) => (
              <button key={val} onClick={() => setOpenWithinFilter(val)} style={{ fontSize: 12, padding: "6px 12px", borderRadius: 6, border: "1px solid", borderColor: openWithinFilter === val ? "#2E75B6" : "#D8DEE4", background: openWithinFilter === val ? "#2E75B6" : "white", color: openWithinFilter === val ? "white" : "#4A5568", cursor: "pointer", fontWeight: openWithinFilter === val ? 600 : 400 }}>
                {label}
              </button>
            ))}
          </div>
         <span style={{ fontSize: 12, color: "#8A95A3", marginLeft: "auto" }}>
            {filtered.length} loaded
            {trackerFilter && ` · ${trackerFilter.replace(/_/g, ' ')}`}
            {filter !== "all" ? ` of ${filter === "included" ? includedCount.toLocaleString() : excludedCount.toLocaleString()}` : ""}
          </span>
        </div>
        {/* Category Filters (#32) */}
        {filter === "included" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "8px 0" }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
              <span style={{ fontSize: 11, color: "#8A95A3", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Filters:</span>
              <select value={minAmount} onChange={e => setMinAmount(e.target.value)} style={{ fontSize: 12, padding: "5px 8px", borderRadius: 6, border: "1px solid", borderColor: minAmount ? "#2B7A4B" : "#D8DEE4", background: minAmount ? "#EBF5EE" : "white", color: minAmount ? "#2B7A4B" : "#4A5568", cursor: "pointer" }}>
                <option value="">Any amount</option>
                <option value="1000">$1K+</option>
                <option value="5000">$5K+</option>
                <option value="10000">$10K+</option>
                <option value="25000">$25K+</option>
              </select>
              <select value={yearLevelFilter} onChange={e => setYearLevelFilter(e.target.value)} style={{ fontSize: 12, padding: "5px 8px", borderRadius: 6, border: "1px solid", borderColor: yearLevelFilter ? "#2B7A4B" : "#D8DEE4", background: yearLevelFilter ? "#EBF5EE" : "white", color: yearLevelFilter ? "#2B7A4B" : "#4A5568", cursor: "pointer" }}>
                <option value="">Year in School</option>
                <option value="freshman">College Freshman</option>
                <option value="sophomore">College Sophomore</option>
                <option value="junior">College Junior</option>
                <option value="senior">College Senior</option>
                <option value="two_year">Community College</option>
                <option value="trade_tech">Trade / Certificate</option>
                <option value="graduate">Graduate Student</option>
                <option value="postgrad">Postgraduate</option>
              </select>
              <select value={majorFilter} onChange={e => setMajorFilter(e.target.value)} style={{ fontSize: 12, padding: "5px 8px", borderRadius: 6, border: "1px solid", borderColor: majorFilter ? "#2B7A4B" : "#D8DEE4", background: majorFilter ? "#EBF5EE" : "white", color: majorFilter ? "#2B7A4B" : "#4A5568", cursor: "pointer", maxWidth: 180 }}>
                <option value="">Any major</option>
                {TOP_MAJORS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
              <div style={{ position: "relative", display: "inline-block" }}>
                <input
                  type="text"
                  placeholder={institutionFilter || "College/University"}
                  value={institutionFilter ? "" : institutionSearch}
                  onChange={e => { setInstitutionSearch(e.target.value); setInstitutionFilter(""); }}
                  onFocus={() => { if (institutionOptions.length > 0) setShowInstitutionDropdown(true); }}
                  onBlur={() => setTimeout(() => setShowInstitutionDropdown(false), 200)}
                  style={{ fontSize: 12, padding: "5px 8px", borderRadius: 6, border: "1px solid", borderColor: institutionFilter ? "#2B7A4B" : "#D8DEE4", background: institutionFilter ? "#EBF5EE" : "white", color: institutionFilter ? "#2B7A4B" : "#4A5568", width: 170, boxSizing: "border-box" }}
                />
                {institutionFilter && (
                  <button onClick={() => { setInstitutionFilter(""); setInstitutionSearch(""); }} style={{ position: "absolute", right: 4, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#2B7A4B", cursor: "pointer", fontSize: 14, fontWeight: 700, padding: "0 2px" }}>✕</button>
                )}
                {showInstitutionDropdown && institutionSearch.length >= 2 && (
                  <div style={{ position: "absolute", top: "100%", left: 0, width: 280, maxHeight: 200, overflowY: "auto", background: "white", border: "1px solid #D8DEE4", borderRadius: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.1)", zIndex: 100 }}>
                    {institutionOptions.length > 0 ? institutionOptions.map(name => (
                      <div key={name} onMouseDown={() => { setInstitutionFilter(name); setInstitutionSearch(""); setShowInstitutionDropdown(false); }} style={{ padding: "8px 10px", fontSize: 12, cursor: "pointer", borderBottom: "1px solid #F0F0F0" }} onMouseOver={e => e.target.style.background="#F7F8FA"} onMouseOut={e => e.target.style.background="white"}>
                        {name}
                      </div>
                    )) : (
                      <div style={{ padding: "10px 12px", fontSize: 12, color: "#8A95A3", lineHeight: 1.5 }}>
                        No scholarships restricted to this school — your matches already include scholarships you can use at any school.
                      </div>
                    )}
                  </div>
                )}
              </div>
              {[
                ["No Essay", noEssay, setNoEssay],
                ["Sport", athleticFilter, setAthleticFilter],
                ["Merit", meritFilter, setMeritFilter],
                ["Need-Based", needFilter, setNeedFilter],
                ["No GPA Req", noGpaFilter, setNoGpaFilter],
              ].map(([label, val, setter]) => (
                <button key={label} onClick={() => setter(!val)} style={{ fontSize: 12, padding: "5px 12px", borderRadius: 6, border: "1px solid", borderColor: val ? "#2B7A4B" : "#D8DEE4", background: val ? "#2B7A4B" : "white", color: val ? "white" : "#4A5568", cursor: "pointer", fontWeight: val ? 600 : 400 }}>
                  {label}
                </button>
              ))}
              <button onClick={() => setShowMoreFilters(!showMoreFilters)} style={{ fontSize: 12, padding: "5px 12px", borderRadius: 6, border: "1px solid #D8DEE4", background: showMoreFilters ? "#F7F8FA" : "white", color: "#4A5568", cursor: "pointer" }}>
                {showMoreFilters ? "Less ▲" : "More ▼"}
              </button>
              {hasActiveFilters && (
                <button onClick={clearCategoryFilters} style={{ fontSize: 11, padding: "5px 10px", borderRadius: 6, border: "1px solid #E53E3E", background: "white", color: "#E53E3E", cursor: "pointer", fontWeight: 600 }}>
                  Clear Filters ✕
                </button>
              )}
            </div>
            {showMoreFilters && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, paddingLeft: 52 }}>
                {[
                  ["Minorities", minoritiesFilter, setMinoritiesFilter],
                  ["First-Gen", firstGenFilter, setFirstGenFilter],
                  ["Military", militaryFilter, setMilitaryFilter],
                  ["Religious", religiousFilter, setReligiousFilter],
                  ["Disability", disabilityFilter, setDisabilityFilter],
                  ["LGBTQ", lgbtqFilter, setLgbtqFilter],
                  ["ROTC", rotcFilter, setRotcFilter],
                  ["Single Parent", singleParentFilter, setSingleParentFilter],
                  ["Residency", residencyFilter, setResidencyFilter],
                ].map(([label, val, setter]) => (
                  <button key={label} onClick={() => setter(!val)} style={{ fontSize: 12, padding: "5px 12px", borderRadius: 6, border: "1px solid", borderColor: val ? "#2B7A4B" : "#D8DEE4", background: val ? "#2B7A4B" : "white", color: val ? "white" : "#4A5568", cursor: "pointer", fontWeight: val ? 600 : 400 }}>
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {/* Results */}
        {loading ? (
          <div style={{ textAlign: "center", padding: "3rem", color: "#8A95A3" }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "3rem", color: "#8A95A3" }}>
            No results match your current filters.
          </div>
        ) : (
          <>
            {sorted.map(match => <ScholarshipCard key={match.match_id} match={match} onTrack={onTrack} studentId={studentId} />)}

            {/* Load More */}
            {hasMore && (
              <div style={{ textAlign: "center", padding: "20px 0" }}>
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  style={{
                    fontSize: 14, padding: "10px 28px", borderRadius: 8,
                    background: loadingMore ? "#AED6F1" : "#2E75B6",
                    color: "white", border: "none",
                    cursor: loadingMore ? "wait" : "pointer",
                    fontWeight: 600
                  }}
                >
                  {loadingMore ? "Loading…" : "Load More Scholarships"}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
      </>
  );
}
